import { AfterViewChecked, ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { MatAccordion, MatExpansionModule } from '@angular/material/expansion';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CdkDrag, CdkDropList } from '@angular/cdk/drag-drop';
import { UtilsService } from '../../services/utils.service';
import { SharedService } from '../../services/shared.service';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmationDialogComponent } from '../confirmation-dialog/confirmation-dialog.component';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
	ClientIdRequest,
	ClientCategoryControllerService as ClippingClientCategoryControllerService,
	ManagementCategoryClippingControllerService,
	ManagementCategoryResponse,
	ManagementTagResponse,
} from 'api-clipping';
import { take } from 'rxjs/internal/operators/take';
import { ActivatedRoute } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { debounceTime } from 'rxjs/internal/operators/debounceTime';
import { distinctUntilChanged } from 'rxjs/internal/operators/distinctUntilChanged';
import { takeUntil } from 'rxjs/internal/operators/takeUntil';
import { Subject } from 'rxjs/internal/Subject';
import { TagsDropAssignService } from '../../services/tags-drop-assign.service';
import {
	ClientCategoryControllerService as ContractNoticesClientCategoryControllerService,
	ManagementCategoryContractNoticesControllerService,
	ManagementCategoryTagResponse,
} from 'api-contract-notices';
import {
	ManagementCategoryTranscriptionControllerService,
	ClientCategoryControllerService as TranscriptionClientCategoryControllerService,
	CategoryTagResponse as TranscriptionCategoryTagResponse,
	ClientTagTranscriptionControllerService,
	ManagementCreateTagRequest as TranscriptionCreateTagRequest,
	ManagementCreateTagDetailsRequest as TranscriptionCreateTagDetailsRequest,
} from 'api-transcription';
import { Observable } from 'rxjs/internal/Observable';
import { of } from 'rxjs';
import { CommonModule } from '@angular/common';

interface ManagementCategoryWithTags extends ManagementCategoryResponse {
	tags?: ManagementTagWithSelect[];
	checked?: boolean;
	isOpened?: boolean;
}

interface ManagementTagWithSelect extends ManagementTagResponse {
	checked?: boolean;
}

/** A tag paired with its owning category, kept only so checkItem() can update the category's
 *  aggregate `checked` and fire the selection-change trigger exactly as it does everywhere else
 *  -- the category itself is never rendered in the brand-split (flat) view. */
interface BrandTagWithCategory {
	tag: ManagementTagWithSelect;
	category: ManagementCategoryWithTags;
}

interface BrandSplitView {
	company: BrandTagWithCategory[];
	competitor: BrandTagWithCategory[];
	thematic: BrandTagWithCategory[];
	hasCompany: boolean;
	hasCompetitor: boolean;
	hasThematic: boolean;
}

@Component({
	selector: 'app-tags',
	standalone: true,
	imports: [
		CommonModule,
		ReactiveFormsModule,
		MatExpansionModule,
		MatCheckboxModule,
		MatFormFieldModule,
		MatInputModule,
		MatSelectModule,
		MatIconModule,
		MatTooltipModule,
		CdkDropList,
		CdkDrag,
		TranslateModule,
		FormsModule,
	],
	templateUrl: './tags.component.html',
	styleUrl: './tags.component.scss',
})
export class TagsComponent implements OnInit, OnDestroy, AfterViewChecked, OnChanges {
	private onDestroy: Subject<any> = new Subject();
	@Input() role!: 'admin' | 'client' | 'landing';
	@Output() actionRequested: EventEmitter<any> = new EventEmitter();

	@ViewChild('tagsWrapperAccordion', { static: false }) accordionWrapper: MatAccordion | undefined;
	tagsWrapperExpanded = true;
	@ViewChild('tagsAccordion', { static: false }) accordion: MatAccordion | undefined;

	searchFormControl: FormControl = new FormControl();
	searchedTerm: string = '';
	searchStore!: ManagementCategoryWithTags[];

	tagType: 'clipping' | 'contract-notices' | 'contract-announcements' | 'transcriptions' = 'clipping';

	categories!: ManagementCategoryWithTags[];
	brandSplit: BrandSplitView = { company: [], competitor: [], thematic: [], hasCompany: false, hasCompetitor: false, hasThematic: false };

	selectedItems: ManagementTagWithSelect[] = [];
	originalTagIds: string[] = []; // Store original preselected tag IDs
	originalPreselectAll: boolean = false; // Store if all tags were preselected

	// Client-side tag creation (transcriptions only): the "+ add tag" input under each category
	newTagName: { [categoryId: string]: string } = {};
	creatingTagFor: string | null = null;
	// Client-side tag deletion (transcriptions only): id of the tag currently being removed
	deletingTagFor: string | null = null;

	constructor(
		private route: ActivatedRoute,
		private apiContractNoticeManagementCategory: ManagementCategoryContractNoticesControllerService,
		private apiContractNoticeClientCategory: ContractNoticesClientCategoryControllerService,
		private apiClippingManagementCategory: ManagementCategoryClippingControllerService,
		private apiClippingClientCategory: ClippingClientCategoryControllerService,
		private apiTranscriptionManagementCategory: ManagementCategoryTranscriptionControllerService,
		private apiTranscriptionClientCategory: TranscriptionClientCategoryControllerService,
		private apiTranscriptionClientTag: ClientTagTranscriptionControllerService,
		public utils: UtilsService,
		private sharedService: SharedService,
		private cdr: ChangeDetectorRef,
		public tagsDropAssign: TagsDropAssignService,
		private translate: TranslateService,
		private dialog: MatDialog
	) {
		this.route.params.pipe(takeUntilDestroyed()).subscribe(params => {
			this.tagType = params['type'];
			this.getTagCategories();
		});

		this.sharedService.requestActionInComponent.pipe(takeUntilDestroyed()).subscribe({
			next: response => {
				if (response.componentName === 'TagsComponent') {
					switch (response.action) {
						case 'DESELECT_TAGS': {
							this.deselectAll();
							break;
						}
						case 'RESELECT_ORIGINAL_TAGS': {
							this.reselectOriginalTags();
							break;
						}
					}
				}
			},
		});

		this.sharedService.companyTagIds.pipe(takeUntilDestroyed()).subscribe(() => this.recomputeBrandSplit());
		this.sharedService.competitorTagIds.pipe(takeUntilDestroyed()).subscribe(() => this.recomputeBrandSplit());
	}

	private recomputeBrandSplit(): void {
		if (this.tagType !== 'clipping' || !this.categories) {
			this.brandSplit = { company: [], competitor: [], thematic: [], hasCompany: false, hasCompetitor: false, hasThematic: false };
			return;
		}

		const companyIds = this.sharedService.companyTagIds.getValue();
		const competitorIds = this.sharedService.competitorTagIds.getValue();
		const company: BrandTagWithCategory[] = [];
		const competitor: BrandTagWithCategory[] = [];
		const thematic: BrandTagWithCategory[] = [];

		this.categories.forEach(cat => {
			(cat.tags || []).forEach(tag => {
				if (companyIds.has(tag.id)) {
					company.push({ tag, category: cat });
				}
				if (competitorIds.has(tag.id)) {
					competitor.push({ tag, category: cat });
				}
				if (!companyIds.has(tag.id) && !competitorIds.has(tag.id)) {
					thematic.push({ tag, category: cat });
				}
			});
		});

		this.brandSplit = {
			company,
			competitor,
			thematic,
			hasCompany: company.length > 0,
			hasCompetitor: competitor.length > 0,
			hasThematic: thematic.length > 0,
		};
	}

	ngOnInit(): void {
		this.searchFormControl.valueChanges.pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.onDestroy)).subscribe(searchTerm => {
			this.search();
		});
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['role']) {
			this.getTagCategories();
		}
	}

	ngAfterViewChecked(): void {
		this.cdr.detectChanges();
	}

	ngOnDestroy(): void {
		this.onDestroy.next('');
	}

	/** Search methods */
	search() {
		this.searchedTerm = this.searchFormControl.value;
		this.categories = JSON.parse(JSON.stringify(this.searchStore));
		if (this.searchedTerm) {
			let matchedTags: ManagementTagWithSelect[] = [];
			this.categories?.forEach(categoryItem => {
				matchedTags = [];
				categoryItem.isOpened = false;
				categoryItem.checked = false;
				categoryItem.tags?.forEach(tag => {
					if (tag.value?.toLowerCase().includes(this.searchedTerm.toLowerCase())) {
						matchedTags.push(tag);
						categoryItem.isOpened = true;
					}
					categoryItem.tags = matchedTags;
				});
			});
		} else {
			this.accordionAction('closeAll');
			this.searchedTerm = '';
		}
	}
	/** (END)Search methods */

	private getCategoriesObservable(request: ClientIdRequest): Observable<any[]> {
		switch (this.tagType) {
			case 'clipping':
				return this.role === 'admin'
					? this.apiClippingManagementCategory.getCategoriesWithTagsForClientMgmt(request)
					: this.apiClippingClientCategory.getCategoriesWithTagsForClient(request);
			case 'contract-notices':
			case 'contract-announcements':
				return this.role === 'admin'
					? this.apiContractNoticeManagementCategory.getCategoriesWithTagsForClientMgmt(request)
					: this.apiContractNoticeClientCategory.getCategoriesWithTagsForClient(request);
			case 'transcriptions':
				return this.role === 'admin'
					? this.apiTranscriptionManagementCategory.getCategoriesWithTagsForClientMgmt(request)
					: this.apiTranscriptionClientCategory.getCategoriesWithTagsForClient(request);
			default:
				return of([]);
		}
	}

	private getTagCategories() {
		if (this.role) {
			//  this logic is added on client demand:
			//     users in client interface for CLIPPING can only see categories and tags defined in the subscription
			//     users in client interface for CONTRACT NOTICES/ANNOUNCEMENTS see all existing categories and tag
			let clientDto: any = null;
			if (this.role === 'client') {
				const loginData = JSON.parse(localStorage.getItem('loginData') || '{}');
				clientDto = loginData ? { clientId: loginData.clientId } : null;
			}

			this.getCategoriesObservable(this.tagType === 'clipping' || this.tagType === 'transcriptions' ? clientDto : null)
				.pipe(take(1))
				.subscribe({
					next: (response: any) => {
						this.categories = response;
						this.searchStore = response;
						this.recomputeBrandSplit();

						this.searchFormControl.enable();
						if (!this.categories.length) {
							this.searchFormControl.disable();
						}

						//** logic for preselecting */
						if (this.role === 'client') {
							this.selectedItems = [];
							this.tagsDropAssign.manuallyselectedTags = this.selectedItems;
							switch (this.tagType) {
								case 'clipping': {
									this.preselect([], true);
									break;
								}
								case 'contract-notices':
								case 'contract-announcements': {
									this.categories.pop(); // remove "Ostanati tagovi"
									this.getCategoriesObservable(clientDto)
										.pipe(take(1))
										.subscribe({
											next: (response: ManagementCategoryTagResponse[]) => {
												const selectedTags: string[] = [];
												response.forEach(category => {
													category.tags?.forEach(tag => {
														if (tag.id) {
															selectedTags.push(tag.id);
														}
													});
												});
												this.preselect(selectedTags);
											},
											error: () => {
												this.preselect([], false);
											},
										});
									break;
								}
								case 'transcriptions': {
									// Expose the client's followed transcription tag IDs so cards can hide tags the client isn't following.
									const followedTagIds = new Set<string>();
									this.categories?.forEach((category: any) => {
										category.tags?.forEach((tag: any) => {
											if (tag?.id) {
												followedTagIds.add(tag.id);
											}
										});
									});
									this.sharedService.followedTranscriptionTagIds.next(followedTagIds);
									// Transcriptions start with every tag unchecked. An empty tagIds filter is
									// treated as "no tag filter", so the client still gets the full list on load.
									this.preselect([], false);
									break;
								}
							}
						}
					},
				});
		}
	}

	accordionAction(action: 'openAll' | 'closeAll' | 'open' | 'close', category?: ManagementCategoryWithTags) {
		switch (action) {
			case 'openAll':
			case 'closeAll': {
				action === 'openAll' ? this.accordion?.openAll() : this.accordion?.closeAll();
				this.categories?.map(category => (category.isOpened = action === 'openAll'));
				break;
			}
			case 'open':
			case 'close': {
				if (category) {
					category.isOpened = action === 'open';
				}
				break;
			}
		}
	}

	triggerTagSelectionChange() {
		if (this.role === 'client') {
			setTimeout(() => {
				this.actionRequested.emit({ action: 'TAGS_SELECTION_CHANGE' });
			}, 10);
		}
	}

	/**
	 * Client creates a personal transcription tag under the given category.
	 * The backend requires value + colorCode + categoryId + details for all langs, and
	 * auto-follows the tag for the client (priority is backend-controlled).
	 */
	createClientTag(category: ManagementCategoryWithTags) {
		const name = (this.newTagName[category.id] || '').trim();
		if (!name || this.creatingTagFor) {
			return;
		}

		this.creatingTagFor = category.id;

		const body: TranscriptionCreateTagRequest = {
			value: name,
			colorCode: '#FFFFFF',
			categoryId: category.id,
			details: (['MK', 'EN', 'AL'] as TranscriptionCreateTagDetailsRequest.LangEnum[]).map(lang => ({ name, lang })),
		};

		this.apiTranscriptionClientTag
			.createTagForClient(body)
			.pipe(take(1))
			.subscribe({
				next: () => {
					this.newTagName[category.id] = '';
					this.creatingTagFor = null;
					this.sharedService.showNotification.next({
						message: this.translate.instant('NOTIFICATIONS.RECORD_CREATED'),
						type: 'SUCCESS',
					});
					// Reload so the new (auto-followed) tag shows up in the tree
					this.getTagCategories();
				},
				error: err => {
					this.creatingTagFor = null;
					// Backend returns 409 for more than one reason on this endpoint (e.g. missing
					// language details); only these two messages get dedicated copy.
					const backendMessage = typeof err.error?.message === 'string' ? err.error.message.toLowerCase() : '';
					const tagLimitReached = err.status === 409 && backendMessage.includes('tag limit reached');
					const alreadyExists = err.status === 409 && backendMessage.includes('keyword already exists');
					this.sharedService.showNotification.next({
						message: this.translate.instant(tagLimitReached ? 'TAG.LIMIT_REACHED' : alreadyExists ? 'TAG.ALREADY_EXISTS_FOR_CLIENT' : 'NOTIFICATIONS.RECORD_ACTION_ERROR'),
						type: 'ERROR',
					});
				},
			});
	}

	/**
	 * Client deletes one of their transcription tags. This is a hard delete on the
	 * backend (the tag is unfollowed AND removed entirely), so we confirm first.
	 */
	deleteClientTag(tag: ManagementTagWithSelect, category: ManagementCategoryWithTags) {
		if (this.deletingTagFor) {
			return;
		}

		const tagName = this.utils.getParameterByLanguageScript(tag, 'name', true) || tag.value || '';

		const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
			width: '500px',
			// CLIPPING-600 Phase 3 Step 3: SETTE look, client only - Admin keeps today's dialog.
			panelClass: this.role === 'client' ? 'confirmation-dialog-panel--client' : undefined,
			data: {
				type: 'delete-confirmation',
				mode: 'DELETE',
				pillInfo: {
					icon: 'sell',
					label: 'TAG.TITLE',
					value: tagName,
					severity: 'low',
				},
			},
		});

		dialogRef
			.afterClosed()
			.pipe(take(1))
			.subscribe(result => {
				if (!result) {
					return;
				}

				this.deletingTagFor = tag.id;
				this.apiTranscriptionClientTag
					.removeTagForClient(tag.id)
					.pipe(take(1))
					.subscribe({
						next: () => {
							this.deletingTagFor = null;
							this.sharedService.showNotification.next({
								message: this.translate.instant('NOTIFICATIONS.RECORD_DELETED'),
								type: 'SUCCESS',
							});
							// Reload so the removed tag disappears from the tree
							this.getTagCategories();
						},
						error: () => {
							this.deletingTagFor = null;
							this.sharedService.showNotification.next({
								message: this.translate.instant('NOTIFICATIONS.RECORD_ACTION_ERROR'),
								type: 'ERROR',
							});
						},
					});
			});
	}

	/** Selection methods */
	preselect(selectedTags: string[], preassignState?: boolean) {
		this.categories?.forEach(category => {
			category.tags?.forEach(tag => {
				if (preassignState || selectedTags.indexOf(tag.id) !== -1) {
					tag.checked = true;
					this.checkItem(true, tag);
					// Keep categories collapsed by default; user expands them manually
					category.checked = preassignState ? preassignState : category.tags?.every(t => t.checked);
				}
			});
		});

		// Store original tag selection for reset functionality
		if (this.role === 'client') {
			this.originalTagIds = selectedTags;
			this.originalPreselectAll = preassignState || false;
		}

		// Re-derive brand split AFTER preselect so spread copies pick up category.checked
		this.recomputeBrandSplit();

		this.triggerTagSelectionChange();
	}

	selectAll(checked: boolean, category: ManagementCategoryWithTags) {
		category.checked = checked;
		if (category.tags) {
			category.tags.forEach(tag => {
				tag.checked = checked;
				this.checkItem(checked, tag);
			});

			this.triggerTagSelectionChange();
		}
	}
	checkItem(checked: boolean, tag: ManagementTagWithSelect, category?: ManagementCategoryWithTags) {
		if (checked) {
			this.selectedItems.push(tag);
		} else {
			this.selectedItems = this.selectedItems.filter(selectedItem => selectedItem.id !== tag.id);
		}

		this.tagsDropAssign.manuallyselectedTags = this.selectedItems;

		if (category) {
			category.checked = category.tags?.length === category.tags?.filter(tag => tag.checked).length;
		}

		if (category) {
			this.triggerTagSelectionChange();
		}
	}
	indeterminate(category: ManagementCategoryWithTags): boolean {
		if (category && category.tags) {
			const checkedItemsCount = category.tags.filter(tag => tag.checked).length;
			return checkedItemsCount !== category.tags.length && checkedItemsCount > 0 && !category.checked;
		}
		return false;
	}
	/**
	 * Global select-all/deselect-all (CLIPPING-586 Part 2): mutates every tag across every
	 * category in one pass, then triggers exactly one filter request - never loops calling
	 * selectAll()/checkItem() per category/tag, which would each fire their own request.
	 */
	toggleAllTags(checked: boolean) {
		this.categories?.forEach(category => {
			category.checked = checked;
			category.tags?.forEach(tag => (tag.checked = checked));
		});
		this.selectedItems = checked ? this.allTagsFlat() : [];
		this.tagsDropAssign.manuallyselectedTags = this.selectedItems;
		if (this.tagType === 'clipping') {
			this.recomputeBrandSplit();
		}
		this.triggerTagSelectionChange();
	}

	allTagsFlat(): ManagementTagWithSelect[] {
		return this.categories?.flatMap(category => category.tags || []) ?? [];
	}

	allTagsChecked(): boolean {
		const tags = this.allTagsFlat();
		return tags.length > 0 && tags.every(tag => tag.checked);
	}

	/**
	 * Per-section select-all/deselect-all for the clipping brand-split view (Company/
	 * Competitor/Thematic have no category level, so they need their own header control,
	 * mirroring the category-level selectAll() above). Same one-pass + single-trigger rule.
	 */
	toggleBrandSection(items: BrandTagWithCategory[], checked: boolean) {
		items.forEach(item => (item.tag.checked = checked));

		const affectedCategories = new Set(items.map(item => item.category));
		affectedCategories.forEach(category => {
			category.checked = category.tags?.length === category.tags?.filter(t => t.checked).length;
		});

		this.selectedItems = this.allTagsFlat().filter(tag => tag.checked);
		this.tagsDropAssign.manuallyselectedTags = this.selectedItems;
		this.triggerTagSelectionChange();
	}

	brandSectionChecked(items: BrandTagWithCategory[]): boolean {
		return items.length > 0 && items.every(item => item.tag.checked);
	}

	deselectAll() {
		this.categories?.forEach(category => {
			category.checked = false;
			category.tags?.forEach(tag => (tag.checked = false));
		});
		this.selectedItems = [];
		this.tagsDropAssign.manuallyselectedTags = this.selectedItems;

		this.triggerTagSelectionChange();
	}

	reselectOriginalTags() {
		// First deselect all tags
		this.categories?.forEach(category => {
			category.checked = false;
			category.tags?.forEach(tag => (tag.checked = false));
		});
		this.selectedItems = [];

		// Then reselect original tags
		this.categories?.forEach(category => {
			category.tags?.forEach(tag => {
				if (this.originalPreselectAll || this.originalTagIds.indexOf(tag.id) !== -1) {
					tag.checked = true;
					this.checkItem(true, tag);
					// Keep categories collapsed by default; user expands them manually
					category.checked = this.originalPreselectAll ? this.originalPreselectAll : category.tags?.every(t => t.checked);
				}
			});
		});

		this.triggerTagSelectionChange();
	}
	/** (END)Selection methods */
}
